import { useEffect, useState } from "react";
import { BellRing, Check, FlaskConical, Mail, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "");

export function PlatformAlerts({ showSimulation = false }) {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/super-admin/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  useEffect(() => { if (user?.is_super_admin) load(); }, [user]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setPreview(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  if (!user?.is_super_admin) return null;

  const markRead = async (id) => { await api.post(`/super-admin/alerts/${id}/read`); load(); };
  const simulate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/super-admin/simulation/trial-expired", {});
      setSim(data); load();
      toast.success(data.manager_email_sent ? `Email enviado a ${data.manager_email_to}` : "Simulación creada (el email no se pudo enviar)");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const openPreview = async (kind = "expired") => {
    const { data } = await api.get("/super-admin/emails/trial-expired-preview", { params: { kind } });
    setPreview(data.html.replace("<head>", '<head><base target="_blank"/>'));
  };

  return (
    <div className="space-y-3 mb-6" data-testid="platform-alerts">
      {alerts.map((a) => (
        <div key={a.id} className="sns-card p-4 !border-2 !border-[var(--coral)] !bg-orange-50 flex items-start gap-3" data-testid={`alert-${a.id}`}>
          <div className="w-10 h-10 rounded-xl bg-[var(--coral)] text-white flex items-center justify-center shrink-0"><BellRing size={18} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[var(--ink)]">
              {a.type === "trial_extended" ? `${a.tenant_name} ha aceptado 14 días más de prueba` : `Se ha acabado la prueba de ${a.tenant_name}`}
            </div>
            <div className="text-sm text-slate-600 break-all">{a.owner_name ? `${a.owner_name} · ` : ""}{a.owner_email} · {fmt(a.created_at)}</div>
            <div className="text-xs text-slate-500 mt-1">
              {a.type === "trial_extended"
                ? `Desbloqueada. La nueva prueba acaba el ${fmt(a.trial_ends_at)}.`
                : "Cuenta bloqueada hasta que pague. Ya se le envió el email con las opciones de pago. Puedes ofrecerle 14 días más desde Usuarios app."}
            </div>
          </div>
          <button onClick={() => markRead(a.id)} className="w-9 h-9 rounded-lg bg-white border border-orange-200 flex items-center justify-center text-orange-700" title="Marcar como visto" data-testid={`alert-read-${a.id}`}>
            <Check size={16} />
          </button>
        </div>
      ))}

      {showSimulation && (
        <div className="sns-card p-5 border border-[#BFD4F5]" data-testid="simulation-card">
          <div className="flex items-center gap-2 font-bold text-[var(--sky-text)]"><FlaskConical size={18} /> Simulación de fin de prueba</div>
          <p className="text-sm text-slate-600 mt-1">Crea una empresa demo con la prueba caducada, envía el email del gerente a {user.email} y te da accesos para probar el bloqueo y el pago con tarjeta de prueba.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={simulate} disabled={busy} className="sns-btn sns-btn-accent disabled:opacity-60" data-testid="btn-run-simulation">{busy ? "Simulando…" : "Simular fin de prueba"}</button>
            <button onClick={() => openPreview()} className="sns-btn bg-[var(--sky)] text-[var(--sky-text)]" data-testid="btn-preview-email"><Mail size={16} /> Ver email fin de prueba</button>
            <button onClick={() => openPreview("extension")} className="sns-btn bg-orange-50 text-orange-700" data-testid="btn-preview-extension-email"><Mail size={16} /> Ver email 14 días más</button>
          </div>
          {sim && (
            <div className="mt-4 text-sm bg-[var(--sky)] rounded-2xl p-4 space-y-1" data-testid="simulation-result">
              <div><b>Empresa:</b> {sim.tenant_name}</div>
              <div><b>Gerente:</b> {sim.manager.email} / {sim.manager.password}</div>
              <div><b>Empleado:</b> {sim.employee.email} / {sim.employee.password}</div>
              <div><b>Tarjeta de prueba:</b> {sim.test_card}</div>
              <div className="text-xs text-slate-600">Cierra sesión y entra con el gerente para ver el bloqueo y pagar; con el empleado verás el aviso.</div>
            </div>
          )}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-3xl w-full max-w-2xl h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="email-preview-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <b>Email al gerente</b>
              <button onClick={() => setPreview(null)} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="email-preview-close"><X size={18} /></button>
            </div>
            <iframe title="email" srcDoc={preview} className="flex-1 w-full" />
          </div>
        </div>
      )}
    </div>
  );
}
